import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { AlertCircle } from 'lucide-react';
import { FormDialog } from '@/components/forms/FormDialog';
import { FormSection } from '@/components/forms/Form';
import { FormField, FormSelect, FormTextarea } from '@/components/forms/FormField';
import { Combobox } from '@/components/forms/Combobox';
import { useEntreprises } from '@/hooks/queries/useEntreprises';
import { useAllActivites } from '@/hooks/queries/useActivites';
import { useAllSousSegments } from '@/hooks/queries/usePerimetres';
import { useTiers } from '@/hooks/queries/useTiers';
import { useUsersList } from '@/hooks/queries/useUsersList';
import {
  usePipelineChamps,
  usePipelineEtapes,
  usePipelines,
} from '@/hooks/queries/useCrmPipelines';
import {
  useChampsValues,
  useUpsertChampValue,
  useUpsertOpportunite,
} from '@/hooks/queries/useCrmOpportunites';
import { useUser } from '@/hooks/useUser';
import { formatDA } from '@/lib/format';
import type { Json, OpportuniteRow } from '@/lib/database.types';
import { ChampsCustomDynamiques } from './ChampsCustomDynamiques';

interface Props {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  /** Entreprise pré-sélectionnée (depuis le contexte de la page). */
  entrepriseId: string;
  editing: OpportuniteRow | null;
  /** Pipeline pré-sélectionné (depuis Kanban par exemple). */
  defaultPipelineId?: string;
  onSaved: (opp: OpportuniteRow | null) => void;
}

export function OpportuniteFormDialog({
  open,
  onOpenChange,
  entrepriseId: defaultEntrepriseId,
  editing,
  defaultPipelineId,
  onSaved,
}: Props): JSX.Element {
  const { user } = useUser();
  const { data: entreprises = [] } = useEntreprises();
  const { data: allActivites = [] } = useAllActivites();
  const { data: allSousSegments = [] } = useAllSousSegments();
  const { data: users = [] } = useUsersList();
  const upsert = useUpsertOpportunite();
  const upsertChampValue = useUpsertChampValue();

  const cloture30 = new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10);

  // ====== State cascade ======
  const [entrepriseId, setEntrepriseId] = useState(defaultEntrepriseId);
  const [activiteId, setActiviteId] = useState('');
  const [sousSegmentId, setSousSegmentId] = useState('');
  const [pipelineId, setPipelineId] = useState('');
  const [etapeId, setEtapeId] = useState('');
  const [tierId, setTierId] = useState('');
  const [titre, setTitre] = useState('');
  const [description, setDescription] = useState('');
  const [montant, setMontant] = useState('0');
  const [devise, setDevise] = useState('DZD');
  const [taux, setTaux] = useState('1');
  const [probabilite, setProbabilite] = useState('50');
  const [dateClotureP, setDateClotureP] = useState(cloture30);
  const [commercialId, setCommercialId] = useState('');
  const [source, setSource] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Champs custom dynamiques
  const [champsValues, setChampsValues] = useState<Record<string, Json>>({});

  // ====== Fetched data ======
  const { data: pipelines = [] } = usePipelines(entrepriseId || null);
  const { data: etapes = [] } = usePipelineEtapes(pipelineId || null);
  const { data: champsCustom = [] } = usePipelineChamps(pipelineId || null);
  const { data: existingChampsValues = {} } = useChampsValues(editing?.id ?? null);
  const existingChampsValuesKey = JSON.stringify(existingChampsValues);
  const { data: tiers = [] } = useTiers({ entrepriseId: entrepriseId || null, search: '' });

  // ====== Derived cascade ======
  const activitesEnt = useMemo(
    () => allActivites.filter((a) => a.entreprise_id === entrepriseId && (a.ordre ?? 0) < 99),
    [allActivites, entrepriseId],
  );
  const sousSegmentsAct = useMemo(
    () => allSousSegments.filter((s) => s.activite_id === activiteId),
    [allSousSegments, activiteId],
  );
  const pipelinesFiltered = useMemo(
    () =>
      pipelines.filter(
        (p) => p.actif && (!activiteId || !p.activite_id || p.activite_id === activiteId),
      ),
    [pipelines, activiteId],
  );
  // Tiers filtrés par activité + sous-segment
  const tierOptions = useMemo(() => {
    let list = tiers;
    if (activiteId)
      list = list.filter(
        (tt) => !tt.activite_principale_id || tt.activite_principale_id === activiteId,
      );
    if (sousSegmentId)
      list = list.filter((tt) => !tt.sous_segment_id || tt.sous_segment_id === sousSegmentId);
    return list.slice(0, 500).map((tt) => ({
      value: tt.id,
      label: `${tt.code ? tt.code + ' · ' : ''}${tt.raison_sociale}`,
      hint: tt.email ?? tt.telephone ?? undefined,
    }));
  }, [tiers, activiteId, sousSegmentId]);

  // ====== Effects : init ======
  useEffect(() => {
    if (!open) return;
    if (editing) {
      setEntrepriseId(editing.entreprise_id);
      setActiviteId(editing.activite_id ?? '');
      setSousSegmentId(editing.sous_segment_id ?? '');
      setPipelineId(editing.pipeline_id);
      setEtapeId(editing.etape_id);
      setTierId(editing.tier_id ?? '');
      setTitre(editing.titre);
      setDescription(editing.description ?? '');
      setMontant(String(editing.montant_estime));
      setDevise(editing.devise);
      setTaux(String(editing.taux_change));
      setProbabilite(String(editing.probabilite));
      setDateClotureP(editing.date_cloture_prevue ?? cloture30);
      setCommercialId(editing.commercial_id ?? '');
      setSource(editing.source ?? '');
      setChampsValues(existingChampsValues);
    } else {
      setEntrepriseId(defaultEntrepriseId);
      setActiviteId('');
      setSousSegmentId('');
      setPipelineId(defaultPipelineId ?? '');
      setEtapeId('');
      setTierId('');
      setTitre('');
      setDescription('');
      setMontant('0');
      setDevise('DZD');
      setTaux('1');
      setProbabilite('50');
      setDateClotureP(cloture30);
      setCommercialId(user?.id ?? '');
      setSource('');
      setChampsValues({});
    }
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editing, defaultEntrepriseId, defaultPipelineId, user?.id, existingChampsValuesKey]);

  // Auto-set étape quand pipeline change
  useEffect(() => {
    if (pipelineId && etapes.length > 0 && !etapes.some((e) => e.id === etapeId)) {
      setEtapeId(etapes[0]?.id ?? '');
    }
  }, [pipelineId, etapes, etapeId]);

  // Auto-prob depuis l'étape
  useEffect(() => {
    if (etapeId) {
      const e = etapes.find((x) => x.id === etapeId);
      if (e) setProbabilite(String(e.probabilite_defaut));
    }
  }, [etapeId, etapes]);

  // Si activité change → reset sous-segment + pipeline si plus compatibles
  useEffect(() => {
    if (sousSegmentId && !sousSegmentsAct.some((s) => s.id === sousSegmentId)) setSousSegmentId('');
    if (pipelineId && !pipelinesFiltered.some((p) => p.id === pipelineId)) setPipelineId('');
  }, [activiteId, sousSegmentsAct, pipelinesFiltered, sousSegmentId, pipelineId]);

  // Si entreprise change → reset toute la cascade
  useEffect(() => {
    setActiviteId('');
    setSousSegmentId('');
    setPipelineId('');
    setEtapeId('');
    setTierId('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entrepriseId]);

  const ttcDzd = (Number(montant) || 0) * (Number(taux) || 1);

  const handleChampChange = (champId: string, value: unknown): void => {
    setChampsValues((cur) => ({ ...cur, [champId]: value as Json }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (!entrepriseId) return setError('Entreprise obligatoire');
    if (!activiteId) return setError('Activité obligatoire');
    if (!pipelineId) return setError('Pipeline obligatoire');
    if (!etapeId) return setError('Étape obligatoire');
    if (!titre.trim()) return setError('Titre obligatoire');
    if (!tierId) return setError('Tiers / client obligatoire');
    const m = Number(montant);
    if (m < 0) return setError('Montant ≥ 0 requis');

    // Valider champs custom obligatoires
    for (const c of champsCustom) {
      if (!c.obligatoire) continue;
      const v = champsValues[c.id];
      if (v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0)) {
        return setError(`Champ obligatoire manquant : ${c.label}`);
      }
    }

    try {
      const opp = await upsert.mutateAsync({
        id: editing?.id,
        pipeline_id: pipelineId,
        etape_id: etapeId,
        entreprise_id: entrepriseId,
        activite_id: activiteId || null,
        tier_id: tierId,
        titre: titre.trim(),
        description: description || null,
        montant_estime: m,
        devise,
        taux_change: Number(taux) || 1,
        probabilite: Number(probabilite),
        date_cloture_prevue: dateClotureP || null,
        commercial_id: commercialId || null,
        source: source || null,
      });

      // Patch sous_segment_id (pas dans UpsertOpportuniteInput) via direct table update
      if (sousSegmentId !== (editing?.sous_segment_id ?? '')) {
        const { supabase } = await import('@/lib/supabase');
        await supabase
          .from('opportunites')
          .update({ sous_segment_id: sousSegmentId || null })
          .eq('id', opp.id);
      }

      // Sauvegarder les champs custom
      for (const champ of champsCustom) {
        const newVal = champsValues[champ.id] ?? null;
        const oldVal = existingChampsValues[champ.id] ?? null;
        if (JSON.stringify(newVal) !== JSON.stringify(oldVal)) {
          await upsertChampValue.mutateAsync({
            opportunite_id: opp.id,
            champ_id: champ.id,
            valeur: newVal,
          });
        }
      }

      toast.success(editing ? 'Opportunité mise à jour' : 'Opportunité créée');
      onSaved(opp);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur inconnue');
    }
  };

  const noPipeline = entrepriseId && pipelinesFiltered.length === 0;

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title={editing ? `Opportunité ${editing.reference}` : 'Nouvelle opportunité'}
      description="Créez l'opportunité avec son contexte complet : entreprise, activité, sous-segment, pipeline et client."
      onSubmit={handleSubmit}
      isSubmitting={upsert.isPending || upsertChampValue.isPending}
      submitDisabled={!!noPipeline}
      size="xl"
    >
      {error && (
        <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
          {error}
        </div>
      )}

      <FormSection title="Identification">
        <FormSelect
          label="Entreprise"
          value={entrepriseId}
          onChange={setEntrepriseId}
          required
          options={[
            { value: '', label: '— Sélectionner —' },
            ...entreprises.map((e) => ({ value: e.id, label: e.nom })),
          ]}
        />
        <FormSelect
          label="Activité"
          value={activiteId}
          onChange={setActiviteId}
          required
          disabled={!entrepriseId}
          options={[
            { value: '', label: '— Sélectionner —' },
            ...activitesEnt.map((a) => ({ value: a.id, label: a.nom })),
          ]}
        />
        <FormSelect
          label="Sous-segment (optionnel)"
          value={sousSegmentId}
          onChange={setSousSegmentId}
          disabled={!activiteId || sousSegmentsAct.length === 0}
          options={[
            { value: '', label: '— Aucun —' },
            ...sousSegmentsAct.map((s) => ({ value: s.id, label: s.nom })),
          ]}
        />
        <FormSelect
          label="Pipeline"
          value={pipelineId}
          onChange={setPipelineId}
          required
          disabled={!activiteId || pipelinesFiltered.length === 0}
          options={[
            { value: '', label: '— Sélectionner —' },
            ...pipelinesFiltered.map((p) => ({ value: p.id, label: p.nom })),
          ]}
        />
        <FormSelect
          label="Étape"
          value={etapeId}
          onChange={setEtapeId}
          required
          disabled={!pipelineId}
          options={[
            { value: '', label: '— Sélectionner —' },
            ...etapes.map((e) => ({
              value: e.id,
              label: `${e.icone_emoji ?? ''} ${e.nom}`,
            })),
          ]}
        />
        <FormField
          label="Titre"
          value={titre}
          onChange={(e) => setTitre(e.target.value)}
          required
          fullWidth
          placeholder="Court résumé de l'opportunité (ex: Formation Excel équipe RH Sonelgaz)"
        />
        {noPipeline && (
          <div className="flex items-start gap-2 rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-xs text-warning sm:col-span-2">
            <AlertCircle className="mt-0.5 h-3.5 w-3.5" />
            <div>
              Aucun pipeline configuré pour cette entreprise. Créez d&apos;abord un pipeline dans
              <a href="/parametres/crm/pipelines" className="ml-1 underline">
                Paramètres &gt; CRM &gt; Pipelines
              </a>
              .
            </div>
          </div>
        )}
      </FormSection>

      <FormSection title="Client">
        <Combobox
          label="Tiers (client / prospect)"
          value={tierId}
          onChange={setTierId}
          options={tierOptions}
          placeholder="Rechercher par nom, code, email…"
          emptyMessage={
            !activiteId
              ? "Choisissez d'abord une activité"
              : 'Aucun tiers correspondant à cette activité / sous-segment'
          }
          fullWidth
          required
          resultCount={tierOptions.length}
          disabled={!entrepriseId}
        />
        <FormSelect
          label="Commercial assigné"
          value={commercialId}
          onChange={setCommercialId}
          options={[
            { value: '', label: '— Aucun —' },
            ...users.map((u) => ({
              value: u.user_id,
              label: u.full_name ?? u.user_id.slice(0, 8),
            })),
          ]}
        />
        <FormField
          label="Source"
          value={source}
          onChange={(e) => setSource(e.target.value)}
          placeholder="Inbound / Salon / Site web / Recommandation…"
        />
      </FormSection>

      <FormSection title="Montants & probabilité">
        <FormField
          label="Montant estimé"
          type="number"
          step="0.01"
          min="0"
          value={montant}
          onChange={(e) => setMontant(e.target.value)}
          required
        />
        <FormSelect
          label="Devise"
          value={devise}
          onChange={(v) => {
            setDevise(v);
            if (v === 'DZD') setTaux('1');
          }}
          options={[
            { value: 'DZD', label: 'DZD' },
            { value: 'EUR', label: 'EUR' },
            { value: 'USD', label: 'USD' },
          ]}
        />
        {devise !== 'DZD' && (
          <FormField
            label="Taux de change → DZD"
            type="number"
            step="0.000001"
            min="0.000001"
            value={taux}
            onChange={(e) => setTaux(e.target.value)}
          />
        )}
        <div className="rounded-md border border-border bg-muted/30 p-2 text-xs">
          <div className="text-muted-foreground">Équivalent DZD</div>
          <div className="num text-sm font-semibold">{formatDA(ttcDzd)}</div>
        </div>
        <FormField
          label="Probabilité (%)"
          type="number"
          step="1"
          min="0"
          max="100"
          value={probabilite}
          onChange={(e) => setProbabilite(e.target.value)}
          hint="Auto-rempli depuis l'étape, modifiable"
        />
        <FormField
          label="Date clôture prévue"
          type="date"
          value={dateClotureP}
          onChange={(e) => setDateClotureP(e.target.value)}
        />
      </FormSection>

      <FormSection title="Description">
        <FormTextarea
          label="Description / besoin client"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          fullWidth
          rows={3}
        />
      </FormSection>

      {champsCustom.length > 0 && (
        <ChampsCustomDynamiques
          champs={champsCustom}
          values={champsValues}
          onChange={handleChampChange}
          etapeId={etapeId || null}
        />
      )}
    </FormDialog>
  );
}
